<template>
    <div class="col" @click="navigate" :class="{ 'cursor-pointer': to }">
        <div class="chart-widget bg-white q-pa-md relative-position">
            <div class="row chart-area">
                <template v-if="chartType == 'card'">
                    <div class="col-auto">
                        <div class="chart-icon row justify-center items-center" :class="[`bg-${chartColor}`]">
                            <q-icon :name="chartIcon" size="2.5em" color="white"></q-icon>
                        </div>
                    </div>
                    <div class="col text-right chart-data">
                        <template v-if="value >= 0">
                            <small class="text-faded" v-html="title"/>
                            <h4 class="q-ma-none text-dark">
                                <i-count-up :start="0" :end="value" :duration="countUpDuration"
                                            :options="countUpOptions"/>
                            </h4>
                        </template>
                        <template v-else>
                            <h6 class="q-ma-none text-dark" v-html="title"/>
                        </template>
                    </div>
                </template>
                <div v-else class="chart-canvas col-12 row items-center justify-center" :class="[`bg-${chartColor}`]">
                    <div class="col" :class="{'col-5': chartType == 'pie'}">
                        <component :is="`${chartType}-chart`" v-model="data" :height="80"></component>
                    </div>
                    <div v-if="chartType == 'pie' || data.datasets.length > 1"
                         class="chart-labels col-auto text-white q-pa-sm">
                        <ul class="q-pa-none q-ma-none">
                            <li v-for="(dataset, i) in chartType != 'pie' ? data.datasets : data.labels"
                                class="ellipsis">
                                <span class="bullet" :style="{backgroundColor: chartType != 'pie' ? getColorAt(i) :
                                 data.datasets[0].backgroundColor[i %  data.datasets[0].backgroundColor.length]}"></span>
                                {{ chartType != 'pie' ? dataset.label : data.labels[i] }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <template v-if="chartType != 'card'">
                <h5 class="q-mt-sm q-mb-none text-dark" v-html="title"/>
                <small class="text-faded" v-html="description"/>
            </template>
            <template v-if="footerText">
                <hr>
                <q-icon :name="footerIcon" :color="footerColor"/>
                <small :class="[`text-${footerColor}`]" v-html="footerText"/>
            </template>
        </div>
    </div>
</template>

<script>
    import {
        QIcon
    } from 'quasar-framework'

    import {Bar, Line, Pie} from 'vue-chartjs'
    import ICountUp from 'vue-countup-v2'

    let mixins = [{
        props: {
            value: {
                type: Object,
                required: true
            }
        },
        mounted() {
            this.renderChart(this.value, this.options)
        }
    }]

    let LineChart = {
        extends: Line,
        mixins,
        props: {
            options: {
                type: Object,
                default: () => ({
                    defaultFontFamily: "'Roboto', '-apple-system', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                    defaultFontSize: 8,
                    maintainAspectRatio: false,
                    responsive: true,
                    title: {display: false},
                    legend: {display: false},
                    layout: {
                        padding: {
                            left: 2,
                            right: 13,
                            top: 13,
                            bottom: 2
                        }
                    },
                    scales: {
                        xAxes: [{
                            gridLines: {
                                drawBorder: false,
                                display: false
                            },
                            ticks: {
                                beginAtZero: true,
                                fontColor: '#fff'
                            }
                        }],
                        yAxes: [{
                            gridLines: {
                                drawBorder: false,
                                color: 'rgba(255,255,255,0.2)',
                                zeroLineColor: 'rgba(255,255,255,0.2)'
                            },
                            ticks: {
                                beginAtZero: true,
                                fontColor: '#fff'
                            }
                        }]
                    }
                })
            }
        }
    }
    let BarChart = {
        extends: Bar,
        mixins,
        props: {
            options: {
                type: Object,
                default: () => ({
                    defaultFontFamily: "'Roboto', '-apple-system', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                    defaultFontSize: 8,
                    maintainAspectRatio: false,
                    responsive: true,
                    title: {display: false},
                    legend: {display: false},
                    layout: {
                        padding: {
                            left: 2,
                            right: 2,
                            top: 13,
                            bottom: 2
                        }
                    },
                    scales: {
                        xAxes: [{
                            gridLines: {
                                drawBorder: false,
                                display: false
                            },
                            ticks: {
                                beginAtZero: true,
                                fontColor: '#fff'
                            },
                            categoryPercentage: 0.9,
                            barPercentage: 0.5
                        }],
                        yAxes: [{
                            gridLines: {
                                drawBorder: false,
                                color: 'rgba(255,255,255,0.2)',
                                zeroLineColor: 'rgba(255,255,255,0.2)'
                            },
                            ticks: {
                                beginAtZero: true,
                                fontColor: '#fff'
                            }
                        }]
                    }
                })
            }
        }
    }
    let PieChart = {
        extends: Pie,
        mixins,
        props: {
            options: {
                type: Object,
                default: () => ({
                    defaultFontFamily: "'Roboto', '-apple-system', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                    defaultFontSize: 8,
                    maintainAspectRatio: false,
                    responsive: true,
                    title: {display: false},
                    legend: {display: false},
                    cutoutPercentage: 80,
                    layout: {
                        padding: {
                            left: 10,
                            right: 10,
                            top: 10,
                            bottom: 10
                        }
                    }
                })
            }
        }
    }

    export default {
        name: 'ChartWidget',

        props: {
            value: {
                type: [Number, Object]
            },
            title: {
                type: String,
                required: true
            },
            description: String,
            chartIcon: String,
            chartColor: {
                type: String,
                default: 'primary'
            },
            chartType: {
                type: String,
                default: 'bar'
            },
            colors: {
                type: Array,
                default: () => [
                    '#2F4858',
                    '#28656E',
                    '#388173',
                    '#6A9A6B',
                    '#ABAD63'
                ]
            },
            countUpDuration: {
                type: Number,
                default: 2
            },
            countUpOptions: {
                type: Object,
                default: () => ({
                    useEasing: true,
                    useGrouping: true,
                    separator: ' ',
                    decimal: ','
                })
            },
            footerIcon: {
                type: String,
                default: 'info'
            },
            footerColor: {
                type: String,
                default: 'faded'
            },
            footerText: String,
            to: {
                type: [String, Object],
                default: null
            }
        },

        components: {
            QIcon,
            LineChart,
            BarChart,
            PieChart,
            ICountUp
        },
        computed: {
            data() {
                if (!this.value.datasets) return

                return {
                    labels: this.value.labels,
                    datasets: this.value.datasets.map((data, i) => (Object.assign({
                        // Common to all charts
                        label: this.value.datasetLabels ? this.value.datasetLabels[i] || '' : '',
                        borderWidth: 1,
                        borderColor: this.getColorAt(i),
                        data
                    }, {
                        // Per chart type options
                        line: {
                            pointBackgroundColor: this.getColorAt(i),
                            pointBorderColor: '#fff',
                            fill: false,
                        },
                        bar: {
                            backgroundColor: this.getColorAt(i),
                        },
                        pie: {
                            backgroundColor: this.colors
                        }
                    }[this.chartType])))
                }
            }
        },
        methods: {
            getColorAt(index) {
                if (index == 0) return '#fff'
                return this.colors[index % this.colors.length]
            },
            navigate() {
                if (this.to) this.$router.push(this.to)
            }
        }
    }
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/ChartWidget.vue